import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface EprelProduct {
  energyClass: string;
  ratedCapacity: number;
  maximumSpinSpeed: number;
  energyAnnual: number;
  waterAnnual: number;
  noiseWashing: number;
  noiseSpinning: number;
  spinDryingClass: string;
  modelIdentifier: string;
  supplierOrTrademark: string;
  eprelRegistrationNumber: string;
  energyConsumption60FullLoad?: number;
  energyConsumption60PartialLoad?: number;
  energyConsumption40PartialLoad?: number;
  programmeTime60FullLoad?: number;
  programmeTime60PartialLoad?: number;
  programmeTime40PartialLoad?: number;
  moisture?: number;
  onMarketStartDate?: number[]; // [year, month, day]
  // 2019 regulation fields (EU 2019/2014) - Washing Machines
  energyConsPer100Cycle?: number;
  energyConsPerCycle?: number;
  energyEfficiencyIndex?: number;
  programmeDurationRated?: number; // Programme duration at rated capacity (minutes)
  programmeDurationHalf?: number;
  programmeDurationQuarter?: number;
  waterCons?: number; // Water consumption per cycle (L)
  noise?: number; // Noise during spinning (dB)
  noiseClass?: string;
  dimensionHeight?: number;
  dimensionWidth?: number;
  dimensionDepth?: number;
  guaranteeDuration?: number; // Commercial guarantee in months
  spinSpeedRated?: number;
  washingEfficiencyIndex?: number;
  rinsingEffectiveness?: number;
  spinClass?: string;
  // Oven-specific EPREL fields
  volume?: number; // Cavity volume in liters
  heatSource?: string; // e.g., "Electric", "Gas"
  numberOfCavities?: number;
  energyConventional?: number; // Energy per cycle conventional mode (kWh)
  energyForcedAirConvection?: number; // Energy per cycle fan-forced mode (kWh)
  // Dishwasher-specific EPREL fields
  ratedCapacityEco?: number; // Place settings for eco programme
  waterConsEco?: number; // Water consumption per cycle eco (L)
  programmeDurationEco?: number; // Duration of eco programme (minutes)
  energyConsEco?: number; // Energy per cycle eco (kWh)
  noiseClassEco?: string; // Noise class for eco programme
  // Tumble Dryer-specific EPREL fields
  condensationEfficiency?: number; // Condensation efficiency %
  condensationEfficiencyClass?: string; // Condensation efficiency class (A-G)
  programmeDurationDrying?: number; // Programme duration for drying (minutes)
  weightedEnergyConsDrying?: number; // Weighted energy consumption per drying cycle (kWh)
  energyConsDry?: number; // Energy consumption per drying cycle (kWh) - 2023/2534 regulation
  noiseDrying?: number; // Noise during drying (dB)
  noiseClassDrying?: string; // Noise class for drying
  programDurationRated?: number; // Programme duration at rated capacity (minutes) - alternate field name
  programDurationHalf?: number; // Programme duration at half load (minutes)
  guranteeDuration?: number; // Commercial guarantee (months) - EPREL API typo variant
  technology?: string; // Dryer technology e.g. "ELECTRIC_HEAT_PUMP_CONDENSER"
  // Refrigerator-specific EPREL fields (EU 2019/2016)
  designType?: string; // e.g., "BUILT_IN", "FREESTANDING"
  annualEnergyConsumption?: number; // Annual energy consumption (kWh/annum)
  volumeChillAndUnfrozen?: number; // Sum of chill + unfrozen compartment volumes (L)
  volumeFrozen?: number; // Sum of frozen compartment volumes (L)
  totalVolume?: number; // Total volume (L)
  noiseEmission?: number; // Airborne acoustical noise (dB)
  noiseEmissionClass?: string; // Noise emission class (A-D)
  // Raw cavity data from EPREL API (for ovens)
  cavities?: Array<{
    volume?: number;
    energyClass?: string;
    energyEfficiencyIndex?: number;
    heatSourceType?: string;
    energyConsumptionCycle?: number;
    energyConsumptionCycleFanForced?: number;
  }>;
}

export interface EprelClassification {
  applianceCategory: 'WASHING_MACHINE' | 'TUMBLE_DRYER' | 'DISHWASHER' | 'WASHER_DRYER' | 'UNKNOWN';
  dryerTechnology: 'heat_pump' | 'condenser' | 'vented' | null;
  dishwasherInstall: 'freestanding' | 'fully_integrated' | 'semi_integrated' | null;
  dishwasherWidth: 'standard_60cm' | 'slim_45cm' | null;
  dishwasherCapacity: number | null;
  classificationSource: 'eprel';
}

export interface EprelSearchResult {
  success: boolean;
  data: EprelProduct | null;
  searchResults?: EprelProduct[];
  source?: string;
  error?: string;
  isPreEprel?: boolean;
  matchType?: 'exact' | 'partial';
  searchedVariant?: string;
  originalModel?: string;
  productGroup?: string; // The EPREL product group that matched (e.g., 'washingmachines2019', 'ovens')
  detectedCategory?: string; // The app category derived from productGroup
  classification?: EprelClassification; // Normalized FixID classification (dryer/dishwasher fields)
}


// Map EPREL product groups to app categories
export const productGroupToCategory = (productGroup: string): string | null => {
  const mapping: Record<string, string> = {
    'washingmachines2019': 'washing-machine',
    'washingmachines': 'washing-machine',
    'dishwashers2019': 'dishwasher',
    'dishwashers': 'dishwasher',
    'refrigeratingappliances2019': 'refrigerator',
    'refrigeratingappliances': 'refrigerator',
    'ovens': 'oven',
    'domesticovens': 'oven',
    'vacuumcleaners': 'vacuum-cleaner',
    'tumbledryers': 'dryer',
    'tumbledryers2019': 'dryer',
    'tumbledryers20232534': 'dryer',
    'washerdriers2019': 'washer-dryer',
  };
  return mapping[productGroup] || null;
};

export interface OnlineProductInfo {
  sources: { url: string; title: string; isSupplierSite: boolean }[];
  specifications: Record<string, string>;
  description: string | null;
  manualUrl: string | null;
}

export interface OnlineSearchResult {
  success: boolean;
  data: OnlineProductInfo | null;
  source?: string;
  query?: string;
  error?: string;
}

// Map EPREL energy class codes to display format
const formatEnergyClass = (energyClass: string): string => {
  const mapping: Record<string, string> = {
    'APPP': 'A+++',
    'APP': 'A++',
    'AP': 'A+',
    'A': 'A',
    'B': 'B',
    'C': 'C',
    'D': 'D',
    'E': 'E',
    'F': 'F',
    'G': 'G',
  };
  return mapping[energyClass] || energyClass;
};

// Convert EPREL data to specifications format for WASHING MACHINES
export const eprelToWashingMachineSpecs = (data: EprelProduct): { label: string; value: string }[] => {
  // Calculate weighted energy consumption per 100 cycles
  let weightedEnergy = "";
  if (data.energyConsPer100Cycle) {
    weightedEnergy = `${data.energyConsPer100Cycle} kWh`;
  } else if (data.energyConsumption60FullLoad && data.energyConsumption40PartialLoad) {
    const calculated = ((data.energyConsumption60FullLoad + data.energyConsumption40PartialLoad) / 2 * 100).toFixed(1);
    weightedEnergy = `${calculated} kWh`;
  } else if (data.energyAnnual) {
    weightedEnergy = `${(data.energyAnnual / 2.2).toFixed(1)} kWh`;
  }
  
  // Programme duration for rated capacity
  let programmeDuration = "";
  if (data.programmeDurationRated) {
    const hours = Math.floor(data.programmeDurationRated / 60);
    const mins = data.programmeDurationRated % 60;
    programmeDuration = hours > 0 ? `${hours}h ${mins}min` : `${mins}min`;
  } else if (data.programmeTime60FullLoad) {
    const hours = Math.floor(data.programmeTime60FullLoad / 60);
    const mins = data.programmeTime60FullLoad % 60;
    programmeDuration = hours > 0 ? `${hours}h ${mins}min` : `${mins}min`;
  }

  const waterConsumption = data.waterCons 
    ? `${data.waterCons} L`
    : data.waterAnnual 
      ? `${Math.round(data.waterAnnual / 220)} L` 
      : "";

  const marketDate = data.onMarketStartDate 
    ? `${data.onMarketStartDate[0]}-${String(data.onMarketStartDate[1]).padStart(2, '0')}-${String(data.onMarketStartDate[2]).padStart(2, '0')}`
    : "";

  let noiseSpinning = "";
  if (data.noise) {
    noiseSpinning = `${data.noise} dB${data.noiseClass ? ` (${data.noiseClass})` : ''}`;
  } else if (data.noiseSpinning) {
    noiseSpinning = `${data.noiseSpinning} dB`;
  }

  let dimensions = "";
  if (data.dimensionHeight && data.dimensionWidth && data.dimensionDepth) {
    dimensions = `${data.dimensionHeight} x ${data.dimensionWidth} x ${data.dimensionDepth} cm`;
  }

  // Keep the full EPREL model identifier intact — splitting on space drops
  // valid model codes that contain spaces (e.g. Candy "ROE H8A2TCEX-S").
  const model = (data.modelIdentifier || "").trim();
  const type = "";

  return [
    // Column 1: Supplier name, Model, Type, Product number, Serial number, Market date, Guarantee
    // Column 2: Energy Class, Dimensions, Rated capacity, Water consumption, Programme duration, Weighted energy, Noise
    { label: "Supplier name", value: data.supplierOrTrademark || "" },
    { label: "Energy Class", value: formatEnergyClass(data.energyClass) },
    { label: "Model", value: model },
    { label: "Overall dimensions (Height x Width x Depth)", value: dimensions },
    { label: "Appliance Type", value: type },
    { label: "Rated capacity", value: data.ratedCapacity ? `${data.ratedCapacity} kg` : "" },
    { label: "Product number", value: "" },
    { label: "Water consumption (per cycle, eco 40-60 programme)", value: waterConsumption },
    { label: "Serial number", value: "" },
    { label: "Programme duration (Rated capacity)", value: programmeDuration },
    { label: "Model placed on the Union market from", value: marketDate },
    { label: "Weighted energy consumption (per 100 cycles, eco 40-60 programme)", value: weightedEnergy },
    { label: "Duration of the commercial guarantee offered by the supplier", value: data.guaranteeDuration ? `${data.guaranteeDuration} months` : "" },
    { label: "Airborne acoustical noise emissions (spinning phase)", value: noiseSpinning },
  ];
};

// Convert EPREL data to specifications format for DOMESTIC OVENS
export const eprelToOvenSpecs = (data: EprelProduct): { label: string; value: string }[] => {
  const marketDate = data.onMarketStartDate 
    ? `${data.onMarketStartDate[0]}-${String(data.onMarketStartDate[1]).padStart(2, '0')}-${String(data.onMarketStartDate[2]).padStart(2, '0')}`
    : "";

  const model = (data.modelIdentifier || "").trim();
  const type = "";

  // Extract cavity data if present (EPREL returns oven specs in cavities array)
  const cavity = data.cavities?.[0];
  
  // Use cavity data or fall back to root-level data
  const volume = data.volume || cavity?.volume;
  const energyClass = data.energyClass || cavity?.energyClass || "";
  const energyEfficiencyIndex = data.energyEfficiencyIndex || cavity?.energyEfficiencyIndex;
  const heatSource = data.heatSource || (cavity?.heatSourceType ? 
    cavity.heatSourceType.charAt(0) + cavity.heatSourceType.slice(1).toLowerCase() : "");
  const energyConventional = data.energyConventional || cavity?.energyConsumptionCycle;
  const energyForcedAirConvection = data.energyForcedAirConvection || cavity?.energyConsumptionCycleFanForced;

  // Note: EPREL oven schema uses 'numberCavities' at root level
  const numberOfCavities = (data as unknown as { numberCavities?: number }).numberCavities || 
                           data.numberOfCavities || 
                           data.cavities?.length;

  return [
    // Column 1: Supplier name, Model, Type, Product number, Serial number, Market date, Trademark
    // Column 2 (Oven-specific): Energy Class, EEI, Volume, Heat source, Cavities, Energy conventional, Energy fan-forced
    { label: "Supplier name", value: data.supplierOrTrademark || "" },
    { label: "Energy Class", value: formatEnergyClass(energyClass) },
    { label: "Model", value: model },
    { label: "Energy Efficiency Index", value: energyEfficiencyIndex ? `${energyEfficiencyIndex}` : "" },
    { label: "Appliance Type", value: type },
    { label: "Volume", value: volume ? `${volume} L` : "" },
    { label: "Product number", value: "" },
    { label: "Heat source", value: heatSource },
    { label: "Serial number", value: "" },
    { label: "Number of cavities", value: numberOfCavities ? `${numberOfCavities}` : "" },
    { label: "Model placed on the Union market from", value: marketDate },
    { label: "Energy consumption per cycle (conventional mode)", value: energyConventional ? `${energyConventional} kWh` : "" },
    // Note: Commercial guarantee is not provided by EPREL for ovens - this field will need manual entry or other data source
    { label: "Duration of the commercial guarantee offered by the supplier", value: data.guaranteeDuration ? `${data.guaranteeDuration} months` : "" },
    { label: "Energy consumption per cycle (fan-forced convection mode)", value: energyForcedAirConvection ? `${energyForcedAirConvection} kWh` : "" },
  ];
};

// Convert EPREL data to specifications format for DISHWASHERS
export const eprelToDishwasherSpecs = (data: EprelProduct): { label: string; value: string }[] => {
  const marketDate = data.onMarketStartDate 
    ? `${data.onMarketStartDate[0]}-${String(data.onMarketStartDate[1]).padStart(2, '0')}-${String(data.onMarketStartDate[2]).padStart(2, '0')}`
    : "";

  // Dimensions
  let dimensions = "";
  if (data.dimensionHeight && data.dimensionWidth && data.dimensionDepth) {
    dimensions = `${data.dimensionHeight} x ${data.dimensionWidth} x ${data.dimensionDepth} cm`;
  }

  // Programme duration for eco
  let programmeDurationEco = "";
  if (data.programmeDurationEco) {
    const hours = Math.floor(data.programmeDurationEco / 60);
    const mins = data.programmeDurationEco % 60;
    programmeDurationEco = hours > 0 ? `${hours}h ${mins}min` : `${mins}min`;
  }

  const model = (data.modelIdentifier || "").trim();
  const type = "";

  return [
    // Column 1: Supplier name, Model, Type, Product number, Serial number, Market date, Guarantee
    // Column 2 (Dishwasher): Energy Class, Dimensions, Rated capacity eco, Water consumption eco, 
    //   Programme duration eco, Energy consumption eco, Noise class eco
    { label: "Supplier name", value: data.supplierOrTrademark || "" },
    { label: "Energy Class", value: formatEnergyClass(data.energyClass) },
    { label: "Model", value: model },
    { label: "Overall dimensions (Height x Width x Depth)", value: dimensions },
    { label: "Appliance Type", value: type },
    { label: "Rated capacity for the eco programme", value: data.ratedCapacityEco ? `${data.ratedCapacityEco} place settings` : (data.ratedCapacity ? `${data.ratedCapacity} place settings` : "") },
    { label: "Product number", value: "" },
    { label: "Water consumption (per cycle, based on the eco programme)", value: data.waterConsEco ? `${data.waterConsEco} L` : (data.waterCons ? `${data.waterCons} L` : "") },
    { label: "Serial number", value: "" },
    { label: "Programme duration for the eco programme", value: programmeDurationEco },
    { label: "Model placed on the Union market from", value: marketDate },
    { label: "Energy consumption (per cycle, based on the eco programme)", value: data.energyConsEco ? `${data.energyConsEco} kWh` : (data.energyConsPerCycle ? `${data.energyConsPerCycle} kWh` : "") },
    { label: "Duration of the commercial guarantee offered by the supplier", value: data.guaranteeDuration ? `${data.guaranteeDuration} months` : "" },
    { label: "Airborne acoustical noise emission class for the eco programme", value: data.noiseClassEco || data.noiseClass || "" },
  ];
};

// Convert EPREL data to specifications format for TUMBLE DRYERS
export const eprelToTumbleDryerSpecs = (data: EprelProduct): { label: string; value: string }[] => {
  const marketDate = data.onMarketStartDate 
    ? `${data.onMarketStartDate[0]}-${String(data.onMarketStartDate[1]).padStart(2, '0')}-${String(data.onMarketStartDate[2]).padStart(2, '0')}`
    : "";

  // Dimensions
  let dimensions = "";
  if (data.dimensionHeight && data.dimensionWidth && data.dimensionDepth) {
    dimensions = `${data.dimensionHeight} x ${data.dimensionWidth} x ${data.dimensionDepth} cm`;
  }

  // Programme duration for drying
  let programmeDurationDrying = "";
  const durationMinutes = data.programmeDurationDrying || data.programDurationRated || data.programmeDurationRated;
  if (durationMinutes) {
    const hours = Math.floor(durationMinutes / 60);
    const mins = durationMinutes % 60;
    programmeDurationDrying = hours > 0 ? `${hours}h ${mins}min` : `${mins}min`;
  }

  // Condensation efficiency
  let condensationEfficiency = "";
  if (data.condensationEfficiency) {
    condensationEfficiency = `${data.condensationEfficiency}%`;
    if (data.condensationEfficiencyClass) {
      condensationEfficiency += ` (${data.condensationEfficiencyClass})`;
    }
  } else if (data.condensationEfficiencyClass) {
    condensationEfficiency = data.condensationEfficiencyClass;
  }

  // Noise
  let noiseDrying = "";
  if (data.noiseDrying) {
    noiseDrying = `${data.noiseDrying} dB`;
    if (data.noiseClassDrying) {
      noiseDrying += ` (${data.noiseClassDrying})`;
    }
  } else if (data.noise) {
    noiseDrying = `${data.noise} dB${data.noiseClass ? ` (${data.noiseClass})` : ''}`;
  }

  const model = (data.modelIdentifier || "").trim();
  const type = "";

  return [
    // Column 1: Supplier name, Model, Type, Product number, Serial number, Market date, Guarantee
    // Column 2 (Tumble Dryer): Energy Class, Dimensions, Rated capacity, Condensation efficiency,
    //   Programme duration, Weighted energy consumption, Noise
    { label: "Supplier name", value: data.supplierOrTrademark || "" },
    { label: "Energy Class", value: formatEnergyClass(data.energyClass) },
    { label: "Model", value: model },
    { label: "Dimensions", value: dimensions },
    { label: "Appliance Type", value: data.technology ? data.technology.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c: string) => c.toUpperCase()) : type },
    { label: "Rated capacity", value: data.ratedCapacity ? `${data.ratedCapacity} kg` : "" },
    { label: "Product number", value: "" },
    { label: "Condensation efficiency", value: condensationEfficiency },
    { label: "Serial number", value: "" },
    { label: "Programme duration - rated capacity", value: programmeDurationDrying },
    { label: "Model placed on the Union market from", value: marketDate },
    { label: "Energy consumption per drying cycle", value: data.energyConsDry ? `${data.energyConsDry} kWh` : (data.weightedEnergyConsDrying ? `${data.weightedEnergyConsDrying} kWh` : (data.energyConsPer100Cycle ? `${(data.energyConsPer100Cycle / 100).toFixed(2)} kWh` : "")) },
    { label: "Duration of the commercial guarantee offered by the supplier", value: (data.guaranteeDuration || data.guranteeDuration) ? `${data.guaranteeDuration || data.guranteeDuration} months` : "" },
    { label: "Acoustical airborne noise emissions", value: noiseDrying },
  ];
};

// Convert EPREL data to specifications format for HOUSEHOLD REFRIGERATORS
export const eprelToRefrigeratorSpecs = (data: EprelProduct): { label: string; value: string }[] => {
  const marketDate = data.onMarketStartDate 
    ? `${data.onMarketStartDate[0]}-${String(data.onMarketStartDate[1]).padStart(2, '0')}-${String(data.onMarketStartDate[2]).padStart(2, '0')}`
    : "";

  let dimensions = "";
  if (data.dimensionHeight && data.dimensionWidth && data.dimensionDepth) {
    dimensions = `${data.dimensionHeight} x ${data.dimensionWidth} x ${data.dimensionDepth} cm`;
  }

  let designType = "";
  if (data.designType) {
    designType = data.designType.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c: string) => c.toUpperCase());
  }

  let noise = "";
  if (data.noiseEmission) {
    noise = `${data.noiseEmission} dB`;
    if (data.noiseEmissionClass) {
      noise += ` (${data.noiseEmissionClass})`;
    }
  } else if (data.noise) {
    noise = `${data.noise} dB${data.noiseClass ? ` (${data.noiseClass})` : ''}`;
  }

  const model = (data.modelIdentifier || "").trim();
  const type = "";

  return [
    { label: "Supplier name", value: data.supplierOrTrademark || "" },
    { label: "Energy Class", value: formatEnergyClass(data.energyClass) },
    { label: "Model", value: model },
    { label: "Design type", value: designType },
    { label: "Appliance Type", value: type },
    { label: "Overall dimensions (Height x Width x Depth)", value: dimensions },
    { label: "Product number", value: "" },
    { label: "Annual energy consumption", value: data.annualEnergyConsumption ? `${data.annualEnergyConsumption} kWh/annum` : (data.energyAnnual ? `${data.energyAnnual} kWh/annum` : "") },
    { label: "Serial number", value: "" },
    { label: "Airborne acoustical noise emissions", value: noise },
    { label: "Model placed on the Union market from", value: marketDate },
    { label: "Sum of the volumes of the chill compartment(s) and the unfrozen compartment(s)", value: data.volumeChillAndUnfrozen ? `${data.volumeChillAndUnfrozen} L` : (data.totalVolume ? `${data.totalVolume} L` : "") },
    { label: "Duration of the commercial guarantee offered by the supplier", value: data.guaranteeDuration ? `${data.guaranteeDuration} months` : "" },
    { label: "Sum of the volumes of the frozen compartment(s)", value: data.volumeFrozen ? `${data.volumeFrozen} L` : "" },
  ];
};

// Convert EPREL data to specifications based on appliance category
export const eprelToSpecifications = (data: EprelProduct, category: string = 'Washing Machine'): { label: string; value: string }[] => {
  const normalizedCategory = category.toLowerCase();
  
  if (normalizedCategory === 'oven' || normalizedCategory === 'ovens') {
    return eprelToOvenSpecs(data);
  }
  
  if (normalizedCategory === 'dishwasher' || normalizedCategory === 'dishwashers') {
    return eprelToDishwasherSpecs(data);
  }
  
  if (normalizedCategory === 'dryer' || normalizedCategory === 'tumble dryer' || normalizedCategory === 'tumbledryers') {
    return eprelToTumbleDryerSpecs(data);
  }

  if (normalizedCategory === 'refrigerator' || normalizedCategory === 'refrigerators' || normalizedCategory === 'refrigerating appliance') {
    return eprelToRefrigeratorSpecs(data);
  }
  
  // Default to washing machine specs
  return eprelToWashingMachineSpecs(data);
};

// Map category to EPREL product group (prefer 2019 regulation for newer products)
const categoryToProductGroup = (category: string): string => {
  const mapping: Record<string, string> = {
    'Washing Machine': 'washingmachines2019',
    'washing-machine': 'washingmachines2019',
    'Dishwasher': 'dishwashers2019',
    'dishwasher': 'dishwashers2019',
    'Refrigerator': 'refrigeratingappliances2019',
    'refrigerator': 'refrigeratingappliances2019',
    'Oven': 'ovens',
    'oven': 'ovens',
    'Vacuum Cleaner': 'vacuumcleaners',
    'vacuum-cleaner': 'vacuumcleaners',
    'Dryer': 'tumbledryers20232534',
    'dryer': 'tumbledryers20232534',
    'Tumble Dryer': 'tumbledryers20232534',
    'tumble-dryer': 'tumbledryers20232534',
    'Washer Dryer': 'washerdriers2019',
    'washer-dryer': 'washerdriers2019',
  };
  return mapping[category] || 'washingmachines2019';
};

export function useEprelSearch() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Search by exact model number first, then fall back to partial/brand
  const searchByModelAndBrand = async (
    modelNumber: string, 
    brandName: string, 
    category: string = 'Washing Machine',
    additionalFields?: {
      productNumber?: string | null;
      productType?: string | null;
    }
  ): Promise<EprelSearchResult | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('eprel-search', {
        body: {
          modelNumber,
          brandName,
          productGroup: categoryToProductGroup(category),
          productNumber: additionalFields?.productNumber,
          productType: additionalFields?.productType,
        },
      });

      if (fnError) {
        throw fnError;
      }

      // Add detected category from product group if available
      const result = data as EprelSearchResult;
      if (result?.productGroup) {
        result.detectedCategory = productGroupToCategory(result.productGroup) || undefined;
      }

      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Legacy: search by brand only (for backwards compatibility)
  const searchByBrand = async (brand: string, category: string = 'Washing Machine'): Promise<EprelSearchResult | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('eprel-search', {
        body: {
          brandName: brand,
          productGroup: categoryToProductGroup(category),
        },
      });

      if (fnError) {
        throw fnError;
      }

      return data as EprelSearchResult;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const searchByEprelReference = async (
    eprelReference: string,
    category: string = 'Washing Machine'
  ): Promise<EprelSearchResult | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('eprel-search', {
        body: {
          eprelReference,
          productGroup: categoryToProductGroup(category),
        },
      });

      if (fnError) {
        throw fnError;
      }

      // Add detected category from product group if available
      const result = data as EprelSearchResult;
      if (result?.productGroup) {
        result.detectedCategory = productGroupToCategory(result.productGroup) || undefined;
      }

      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Search online for product info (fallback when EPREL not available)
  const searchOnlineProductInfo = async (
    modelNumber: string,
    brand: string,
    category: string = 'Washing Machine'
  ): Promise<OnlineSearchResult | null> => {
    const trimmedModel = (modelNumber || '').trim();
    if (!trimmedModel) {
      // Nothing to search for — skip the edge function call to avoid a 400.
      setError(null);
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('product-info-search', {
        body: {
          modelNumber: trimmedModel,
          brand: (brand || '').trim() || 'Unknown',
          category,
        },
      });

      if (fnError) {
        throw fnError;
      }

      return data as OnlineSearchResult;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    searchByModelAndBrand,
    searchByBrand,
    searchByEprelReference,
    searchOnlineProductInfo,
    eprelToSpecifications,
    isLoading,
    error,
  };
}
